import React, { useContext, useEffect, useMemo } from 'react';
import { Button, FeaturedIcon, Tooltip } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import { Calendar, CheckCircle, HelpCircle, MarkerPin01, User01 } from '@untitled-ui/icons-react';
import moment, { Moment } from 'moment';
import { useLocation, useNavigate } from "react-router-dom";

import DoctorIcon from 'src/assets/doctor.svg?react';
import { useSelector } from 'src/store';
import { isFaceToFace } from 'src/utils/appointmentTypes';
import { CalendarButtons } from 'src/components/CalendarButtons';
import { patientService } from 'src/services/patient';
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { clinicInfoContext } from 'src/contexts/clinicInfo';
import { clinicName, doctorName } from 'src/utils/name';
import { innerHtml, stripHtml } from 'src/utils/html';
import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { isRequestError } from 'src/utils/requestError';

function isWeekend(date: Moment) {
    const dayOfWeek = date.day();
    return dayOfWeek === 0 || dayOfWeek === 6;
}

export function BookingConfirmed({ unauthorized, resend }: { unauthorized: string, resend: string }) {
    useBookingFlowStep(5);
    const location = useLocation();
    const navigate = useNavigate();
    const { confirmNotifyFailed } = location.state || {}; // Destructure passed state
    const { dateTime, clinicId } = useSelector((state) => state.appointment);
    const { data: clinic, ...clinicRequest } = useClinicSelector();
    const { data: doctor, ...doctorRequest } = useDoctorSelector();
    const { data: appointmentType, ...appointmentTypeRequest } = useAppointmentTypeSelector();
    const { setOpen: setViewingClinicInfo } = useContext(clinicInfoContext);

    const { data: basicDetails, invoke: getBasicDetails, ...basicDetailsRequest } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService,
        invokeOnMount: false
    }, clinicId);

    useEffect(() => {
        (async () => {
            try {
                await getBasicDetails();
            } catch (e) {
                if (isRequestError(e) && e.statusCode === 401) {
                    // session has expired
                    navigate(unauthorized, { replace: true });
                } else {
                    throw e;
                }
            }
        })();
    }, [getBasicDetails, navigate, unauthorized])

    const appointmentTime = useMemo(() => moment(dateTime), [dateTime]);

    return <>
        <FeaturedIcon size='lg' color='success' icon={CheckCircle} className='mx-auto mb-2.5' />
        <h1 className='text-2xl text-gray-900 font-semibold text-center'>Thank you, your booking is confirmed!</h1>

        <div className='flex flex-col items-center text-center my-6'>
            <div className='rounded-full overflow-hidden bg-gray-300 h-14 w-14 mb-1'>
                {doctor &&
                    <img className="animate-fade-in w-full h-full object-fit-cover"
                        src={doctor.umbracoProfileUrl ? `${import.meta.env.VITE_MYHEALTH_WEBSITE}${doctor.umbracoProfileUrl}` : doctor.automedProfileUrl}
                    />}
            </div>
            <p className=''>You have an appointment with</p>
            <h2 className='mt-1 text-lg text-gray-900 font-semibold'>
                {doctor ? doctorName(doctor) : doctorRequest.error?.message}
            </h2>
        </div>

        {(confirmNotifyFailed && confirmNotifyFailed === true)
            ? <div className="flex flex-row gap-2.5 mt-5 py-6 px-4 border border-gray-200 rounded-xl text-base bg-white">
                <FeaturedIcon size='md' color='error' />
                <div>
                    <p className='text-error-700 mt-2 mb-2'> Confirmation sending error </p>
                    <p className=" text-sm">
                        There was an issue sending a confirmation to your email and mobile.
                        Please click below and we'll resend it.
                    </p>
                    <p className="text-primary cursor-pointer font-bold mt-2" onClick={() => navigate(resend)}>Resend</p>
                </div>
            </div>
            : <div className="text-base bg-[#FCF3F2]">
                <p className='text-primary-700 text-center '>
                    A confirmation has been sent to your mobile and email!✨
                </p>
            </div>
        }
        <div className='mt-5 py-6 px-4 border border-gray-200 rounded-xl bg-white'>
            <ul className='text-gray-700 font-medium text-base space-y-4'>
                <li className='flex flex-row gap-2.5'>
                    <User01 className='w-6 h-6 text-gray-400' />
                    {basicDetailsRequest.isLoading
                        ? <span className="w-40 opacity-25 animate-pulse inline-block bg-current rounded-full my-[0.5em] h-[0.5em] align-text-bottom"></span>
                        : basicDetails ? `${basicDetails.firstname} ${basicDetails.surname}` : basicDetailsRequest.error?.message}
                </li>
                <li className='flex flex-row gap-2.5'>
                    <DoctorIcon className='w-6 h-6 text-gray-400' />
                    {appointmentTypeRequest.isLoading
                        ? <span className="w-40 opacity-25 animate-pulse inline-block bg-current rounded-full my-[0.5em] h-[0.5em] align-text-bottom"></span>
                        : appointmentType &&
                        <p>
                            {isFaceToFace(appointmentType) ? "Face to Face" : "Telehealth"}<br />
                            <small className='text-gray-600 text-sm font-normal'>
                                {appointmentType.appointmentName}
                                {' '}
                                {appointmentType.apptInstructionsInHTML &&
                                    <Tooltip
                                        tooltipContent={<div {...innerHtml(appointmentType.apptInstructionsInHTML)} />}
                                        attachmentPoint='top'
                                        className='inline-block align-middle hidden'
                                    >
                                        <HelpCircle className='w-4 h-4 text-gray-400' />
                                    </Tooltip>
                                }
                            </small>
                        </p>
                    }
                </li>
                <li className='flex flex-row gap-2.5'>
                    <Calendar className='w-6 h-6 text-gray-400' />
                    {`${appointmentTime.format("dddd, D MMMM YYYY")} at ${appointmentTime.format("LT")}`}
                </li>
                {appointmentType && isFaceToFace(appointmentType) &&
                    <li className='flex flex-row gap-2.5'>
                        <MarkerPin01 className='w-6 h-6 text-gray-400' />
                        <p>
                            {clinicRequest.isLoading
                                ? <span className="w-40 opacity-25 animate-pulse inline-block bg-current rounded-full h-[0.5em] my-[0.5em] h-[0.5em] align-text-bottom"></span>
                                : clinic ? clinicName(clinic) : clinicRequest.error?.message}<br />
                            <button onClick={() => setViewingClinicInfo(true)} className='text-gray-600 text-sm font-semibold underline'>View info</button>
                        </p>
                    </li>}
            </ul>

            <div className='mt-6 w-full grid grid-cols-1 gap-1'>
                {clinic && appointmentType && doctor &&
                    <CalendarButtons
                        startDate={appointmentTime}
                        endDate={moment(appointmentTime).add(appointmentType.standardDurationInMins, "minutes")}
                        title={`${clinicName(clinic)}: ${appointmentType.appointmentName}`}
                        description={`Your appointment is with ${doctorName(doctor)} for ${appointmentType.appointmentName}.`}
                        address={stripHtml(clinic.addressHtml)}
                        geo={{ lat: clinic.latitude, lng: clinic.longitude }}
                    />
                }
            </div>


            {clinic && <div className='mt-5'>
                <h3 className='text-gray-900 text-lg font-semibold mb-1'>Important Information</h3>
                <div className='prose-sm'
                    {...innerHtml((isWeekend(appointmentTime) && clinic.confirmAppHolidayInfo ? clinic.confirmAppHolidayInfo : clinic.confirmAppInfo)
                        .replace(/text-center/g, "text-left")
                        .replace(/<\/?(?:h1|h2|h3|h4|h5|h6|p)>\s*(<br\s?\/?>)/g, "")
                    )}>
                </div>
            </div>}
        </div>

        <div className=" flex flex-col items-center text-center my-5">
            <p className='text-balance'>If you'd like to make any changes to your appointment, please contact the clinic.</p>
            {clinic && <Button
                variant='link'
                color='gray'
                className="mt-6 cursor-pointer"
                onClick={() => {
                    window.close();
                    if (!window.closed) {
                        window.location.href = `${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic!.url}`;
                    }
                }}>
                Book another appointment
            </Button>}
        </div>
    </>
}